import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler,
} from 'chart.js';
import { Calendar, TrendingUp, BarChart3, Download, Share } from 'lucide-react';
// Firebase imports - Supabase removed

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

interface ProductionGraphsProps {
  wells: any[];
}

export default function ProductionGraphs({ wells }: ProductionGraphsProps) {
  const [selectedWells, setSelectedWells] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [chartType, setChartType] = useState('line');
  const [productionData, setProductionData] = useState({});
  const [loading, setLoading] = useState(false);

  // Auto-select first 3 wells if none selected and wells are available
  useEffect(() => {
    if (wells.length > 0 && selectedWells.length === 0) {
      setSelectedWells(wells.slice(0, 3).map(w => w.id));
    }
  }, [wells]);

  useEffect(() => {
    if (selectedWells.length > 0) {
      loadProductionData();
    }
  }, [selectedWells, timeRange]);

  const loadProductionData = async () => {
    setLoading(true);
    try {
      // For demo mode, use entries from wells directly
      const dataMap = {};
      selectedWells.forEach((wellId) => {
        const well = wells.find(w => w.id === wellId);
        if (well?.entries) {
          dataMap[wellId] = filterDataByTimeRange(well.entries, timeRange);
        } else {
          dataMap[wellId] = [];
        }
      });
      
      setProductionData(dataMap);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('Error loading production data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDataByTimeRange = (data: any[], range: string) => {
    const now = new Date();
    let cutoffDate = new Date();
    
    switch (range) {
      case '7d':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        cutoffDate.setDate(now.getDate() - 90);
        break;
      case '1y':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return data;
    }
    
    return data.filter(entry => new Date(entry.timestamp) >= cutoffDate);
  };

  const getChartData = () => {
    const colors = [
      'rgb(59, 130, 246)', // blue
      'rgb(34, 197, 94)', // green
      'rgb(239, 68, 68)', // red
      'rgb(168, 85, 247)', // purple
      'rgb(245, 158, 11)', // yellow
      'rgb(236, 72, 153)', // pink
    ];

    // Get all unique timestamps and sort them
    const allTimestamps = new Set();
    selectedWells.forEach((wellId) => {
      const wellData = productionData[wellId] || [];
      wellData.forEach(entry => {
        if (entry.timestamp) {
          allTimestamps.add(new Date(entry.timestamp).toLocaleDateString());
        }
      });
    });
    
    const labels = Array.from(allTimestamps).sort((a, b) => 
      new Date(a).getTime() - new Date(b).getTime()
    );

    const datasets = [];
    let colorIndex = 0;

    selectedWells.forEach((wellId) => {
      const wellData = productionData[wellId] || [];
      const well = wells.find(w => w.id === wellId);
      
      if (wellData.length === 0) return;

      // Create data arrays aligned with labels
      const oilData = labels.map(label => {
        const entry = wellData.find(e => 
          e.timestamp && new Date(e.timestamp).toLocaleDateString() === label
        );
        return entry ? (entry.oilProduction || 0) : 0;
      });

      const waterData = labels.map(label => {
        const entry = wellData.find(e => 
          e.timestamp && new Date(e.timestamp).toLocaleDateString() === label
        );
        return entry ? (entry.waterProduction || 0) : 0;
      });

      // Oil production dataset
      datasets.push({
        label: `${well?.name || wellId} - Oil`,
        data: oilData,
        borderColor: colors[colorIndex % colors.length],
        backgroundColor: colors[colorIndex % colors.length] + '20',
        tension: 0.1,
        fill: false,
      });

      // Water production dataset
      datasets.push({
        label: `${well?.name || wellId} - Water`,
        data: waterData,
        borderColor: colors[(colorIndex + 1) % colors.length],
        backgroundColor: colors[(colorIndex + 1) % colors.length] + '20',
        tension: 0.1,
        fill: false,
      });

      colorIndex += 2;
    });

    return {
      labels,
      datasets,
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Production Analysis',
      },
    },
    scales: {
      x: {
        type: 'category' as const,
        display: true,
        title: {
          display: true,
          text: 'Date',
        },
      },
      y: {
        type: 'linear' as const,
        beginAtZero: true,
        display: true,
        title: {
          display: true,
          text: 'Barrels Per Day (BPD)',
        },
      },
    },
  };

  const handleWellSelection = (wellId: string) => {
    setSelectedWells(prev => 
      prev.includes(wellId) 
        ? prev.filter(id => id !== wellId)
        : [...prev, wellId]
    );
  };

  const exportChart = () => {
    // In a real app, this would export the chart as PDF/Excel
  };

  const shareChart = () => {
    // In a real app, this would generate a shareable link
  };

  const calculateAverages = () => {
    const averages = {};
    
    selectedWells.forEach(wellId => {
      const wellData = productionData[wellId] || [];
      if (wellData.length === 0) return;
      
      const oilSum = wellData.reduce((sum, entry) => sum + (entry.oilProduction || 0), 0);
      const waterSum = wellData.reduce((sum, entry) => sum + (entry.waterProduction || 0), 0);
      
      averages[wellId] = {
        oil: (oilSum / wellData.length).toFixed(1),
        water: (waterSum / wellData.length).toFixed(1),
        total: ((oilSum + waterSum) / wellData.length).toFixed(1),
      };
    });
    
    return averages;
  };

  const averages = calculateAverages();

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <span>Production Analytics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Well Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Wells</label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {wells.map(well => (
                  <div key={well.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={well.id}
                      checked={selectedWells.includes(well.id)}
                      onChange={() => handleWellSelection(well.id)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor={well.id} className="text-sm truncate">
                      {well.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Time Range */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Time Range</label>
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 Days</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                  <SelectItem value="90d">Last 90 Days</SelectItem>
                  <SelectItem value="1y">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Chart Type */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Chart Type</label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="line">Line Chart</SelectItem>
                  <SelectItem value="bar">Bar Chart</SelectItem>
                  <SelectItem value="scatter">Scatter Plot</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Actions</label>
              <div className="flex space-x-2">
                <Button size="sm" variant="outline" onClick={exportChart}>
                  <Download className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={shareChart}>
                  <Share className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      {selectedWells.length === 0 ? (
        <Card>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center text-gray-500">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Select wells to view production analytics</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="charts" className="w-full">
          <TabsList>
            <TabsTrigger value="charts">Charts</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>

          <TabsContent value="charts" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Production Chart</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="h-96">
                    {(() => {
                      try {
                        const chartData = getChartData();
                        if (!chartData.datasets || chartData.datasets.length === 0) {
                          return (
                            <div className="flex items-center justify-center h-full text-gray-500">
                              <p>No data available for selected wells</p>
                            </div>
                          );
                        }
                        
                        switch (chartType) {
                          case 'line':
                            return <Line data={chartData} options={chartOptions} />;
                          case 'bar':
                            return <Bar data={chartData} options={chartOptions} />;
                          case 'scatter':
                            return <Scatter data={chartData} options={chartOptions} />;
                          default:
                            return <Line data={chartData} options={chartOptions} />;
                        }
                      } catch (error) {
                        console.error('Chart rendering error:', error);
                        return (
                          <div className="flex items-center justify-center h-full text-red-500">
                            <p>Error loading chart. Please try again.</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="summary" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedWells.map(wellId => {
                const well = wells.find(w => w.id === wellId);
                const avg = averages[wellId];
                
                if (!avg) return null;
                
                return (
                  <Card key={wellId}>
                    <CardHeader>
                      <CardTitle className="text-lg">{well?.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Oil Production:</span>
                        <Badge className="bg-green-100 text-green-800">
                          {avg.oil} BPD
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg Water Production:</span>
                        <Badge className="bg-blue-100 text-blue-800">
                          {avg.water} BPD
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Average:</span>
                        <Badge className="bg-gray-100 text-gray-800">
                          {avg.total} BPD
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="trends" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Production Trends Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {selectedWells.map(wellId => {
                    const well = wells.find(w => w.id === wellId);
                    const wellData = productionData[wellId] || [];
                    
                    if (wellData.length < 2) return null;
                    
                    const firstEntry = wellData[0];
                    const lastEntry = wellData[wellData.length - 1];
                    const oilTrend = ((lastEntry.oilProduction || 0) - (firstEntry.oilProduction || 0)) / (firstEntry.oilProduction || 1) * 100;
                    const waterTrend = ((lastEntry.waterProduction || 0) - (firstEntry.waterProduction || 0)) / (firstEntry.waterProduction || 1) * 100;
                    
                    return (
                      <div key={wellId} className="border rounded-lg p-4">
                        <h4 className="font-medium mb-2">{well?.name}</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Oil Trend:</span>
                            <span className={`ml-2 font-medium ${oilTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {oilTrend >= 0 ? '+' : ''}{oilTrend.toFixed(1)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">Water Trend:</span>
                            <span className={`ml-2 font-medium ${waterTrend >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {waterTrend >= 0 ? '+' : ''}{waterTrend.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}